/**
 * The ONE place cabin values cross the UI/wire boundary.
 *
 * NOTE: unrelated to the backoffice `flightClass` / `tbl_flight_class` module,
 * which is charter seat allotment (A-Z bucket letters). Never merge the two.
 *
 * There is NO "all classes" option. It used to exist and serialize to
 * `undefined` (= do not constrain the search), which worked on Amadeus but was a
 * lie on Cockpit: `cockpit-api.service.ts` sends `['ECONOMY']` whenever no cabin
 * is chosen, so "all classes" silently meant economy-only there, and premium
 * rows in a merged result list could only ever come from Amadeus. Every cabin
 * value is now a real cabin, `travelClass` is always sent, and the two providers
 * finally answer the same question.
 *
 * The deliberate cost: an Amadeus search that previously returned every cabin is
 * now economy-filtered by default, on one-way, round-trip and dynamic-package
 * searches alike. That is a platform-wide narrowing, accepted so that "economy"
 * means economy everywhere.
 */
export type CabinOption = 'economy' | 'premium_economy' | 'business' | 'first';

export const CABIN_OPTION_ORDER: CabinOption[] = [
  'economy', 'premium_economy', 'business', 'first',
];

/** What a search defaults to when nothing is chosen or the value is unusable. */
export const DEFAULT_CABIN: CabinOption = 'economy';

/**
 * Narrow an untrusted `searchParams.cabin` to a real cabin.
 *
 * `searchParams` is a `Record<string, any>` populated from the URL, so its
 * `cabin` is whatever text a shared, bookmarked or hand-edited link carried —
 * including `all`, the pseudo-option this module deliberately removed (see the
 * header) and which is still sitting in older links. Call sites were writing
 * `searchParams.cabin ?? DEFAULT_CABIN`, which only defends against
 * null/undefined: any other string passed straight through wearing a
 * `CabinOption` type it did not satisfy, and then quietly fell out of every
 * `Record<CabinOption, …>` lookup as `undefined`.
 *
 * Falls back to DEFAULT_CABIN rather than returning undefined, because every
 * caller of this is a SEARCH selection — the one context where "we do not know"
 * has no useful meaning. `fromWireCabin` remains the nullable decoder for the
 * display path, where an unknown delivered cabin is a real answer.
 */
export const asCabinOption = (value?: unknown): CabinOption =>
  CABIN_OPTION_ORDER.includes(value as CabinOption)
    ? (value as CabinOption)
    : DEFAULT_CABIN;

const TO_WIRE: Record<CabinOption, string> = {
  economy: 'ECONOMY',
  premium_economy: 'PREMIUM_ECONOMY',
  business: 'BUSINESS',
  first: 'FIRST',
};

const FROM_WIRE: Record<string, CabinOption> = {
  ECONOMY: 'economy',
  PREMIUM_ECONOMY: 'premium_economy',
  BUSINESS: 'business',
  FIRST: 'first',
};

export const toWireCabin = (option: CabinOption): string | undefined => TO_WIRE[option];

/**
 * Decode a wire value, or `undefined` when it is absent/unrecognised.
 *
 * `undefined` rather than a fallback cabin because this serves the DISPLAY path
 * too (which cabin a segment was actually delivered in), and "we do not know" is
 * a real, distinct answer there — claiming economy would invent a fact about a
 * booked ticket. Callers choosing a SEARCH default should use `DEFAULT_CABIN`.
 */
export const fromWireCabin = (wire?: string): CabinOption | undefined =>
  (wire && FROM_WIRE[String(wire).toUpperCase()]) || undefined;

/**
 * Merge the cabin into an outgoing flight search payload. Still a no-op for
 * null/undefined — `params` comes back untouched rather than gaining an empty
 * `travelClass` key — but there is no longer a cabin value that means "send
 * nothing", so in practice every search now carries one.
 *
 * `cabin` is typed `unknown`, not `CabinOption | null`, on purpose: every
 * real call site destructures it out of a `Record<string, any|unknown>` bag
 * of leftover Redux `searchParams`, so it arrives untyped at the call site,
 * not as the narrowed `CabinOption` this module defines for its own internal
 * use. `unknown` (rather than `any`) still forces the explicit cast below —
 * this is the one place a value from the wildly-typed `searchParams` bag
 * becomes a real provider instruction (`travelClass`), so it should not
 * silently accept a typo'd property access the way `any` would.
 */
export const withCabinParam = <T extends Record<string, any>>(
  params: T,
  cabin?: unknown,
): T & { travelClass?: string } => {
  const wire = cabin ? toWireCabin(cabin as CabinOption) : undefined;
  return wire ? { ...params, travelClass: wire } : params;
};

/** Translation keys, in display order. Labels come from `searchDatePicker`. */
export const CABIN_LABEL_KEYS: Record<CabinOption, string> = {
  economy: 'economy',
  premium_economy: 'premiumEconomy',
  business: 'business',
  first: 'firstClass',
};

/**
 * antd preset tag colours, one per cabin, for surfaces that show WHICH cabin was
 * searched (the multi-city results header today).
 *
 * Lives here rather than in the component so that adding a cabin means touching
 * one file: this module already owns the wire mapping, the display order and the
 * label keys, and a colour map kept elsewhere would be the one thing a new
 * `CabinOption` could silently miss — `Record<CabinOption, …>` makes that a type
 * error instead.
 *
 * Preset names, not hex: antd resolves them against the tenant's own
 * `antd.variable` theme, so a tenant restyling its palette restyles these too
 * rather than fighting a hardcoded swatch. Chosen to RANK — cool for economy
 * climbing to warm/premium for the front of the cabin — so the tag reads as a
 * tier at a glance and not as an arbitrary category colour.
 */
export const CABIN_TAG_COLORS: Record<CabinOption, string> = {
  economy: 'blue',
  premium_economy: 'cyan',
  business: 'gold',
  first: 'purple',
};

/**
 * Retained as an alias of CabinOption now that no pseudo-option exists. Kept so
 * the delivered-cabin call sites keep reading as "a real cabin was delivered"
 * rather than becoming indistinguishable from a search selection.
 */
export type RealCabin = CabinOption;

/**
 * Low -> high "what cabin was actually delivered" rank, used to
 * pick the highest cabin when a leg's segments disagree. Deliberately a
 * SEPARATE constant from CABIN_OPTION_ORDER, which is UI display order for
 * the FlightSearch cabin dropdown — the two happen to agree today, but
 * reordering the dropdown for a product reason must never silently change
 * what "highest cabin" means. `cabinClass.test.ts` pins this exact ordering.
 */
const CABIN_RANK: RealCabin[] = ['economy', 'premium_economy', 'business', 'first'];

/** The highest-ranked cabin among the given (non-empty) list. */
export const highestCabin = (options: RealCabin[]): RealCabin =>
  options.reduce((best, current) =>
    CABIN_RANK.indexOf(current) > CABIN_RANK.indexOf(best) ? current : best
  );
